import React, { useEffect, useState } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import SETTINGS from "../../globals";
import UploadFileType from "../../model/enums/UploadFileType";
import { IApiClientError } from "../../shared/ApiClient";
import UploadManager from "../../shared/UploadManager";
import APIEndPoints from "../../utils/APIEndPoints";
import APIError from "../../utils/APIError";
import AppSubSections from "../../utils/AppSubSections";
import ImgUtil from "../../utils/ImgUtil";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import { EXPLANATION_HEADER } from "../common/dialog_window/LicenseDialog";
import FormContainer from "../common/FormContainer";
import FormInputImage from "../common/FormInputImage";
import FormInputText from "../common/FormInputText";
import FormTextArea from "../common/FormTextArea";
import Loading from "../common/Loading";
import MyList from "../../model/MyList";
import AppSections from "../../utils/AppSections";

interface IMyListsFormErrors {
  name: string;
  description: string;
}

const defaultFormErrors: IMyListsFormErrors = {
  name: "",
  description: "",
};

function MyListAddEdit() {
  // read the ID from the url
  const { id } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<MyList>(MyList.EmptyInstance());
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<IMyListsFormErrors>(defaultFormErrors);

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // instance of UploadManager
  const [uploadManager] = useState(new UploadManager({ apiClient: apiClient }));

  const [agreeTerms, setAgreeTerms] = useState<boolean>(false);

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let myList: MyList | undefined = undefined;
        if (id) {
          myList = await apiClient?.get<MyList>({
            url: APIEndPoints.MY_LISTS.ONE(parseInt(id)),
          });
        }

        // set into the state all the information
        setFormData(myList ? myList : MyList.EmptyInstance());
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // get the errors from the form
    const errors = validateListForm(formData, "List");

    if (isFormInvalid(errors)) {
      setFormErrors(errors);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
      return;
    }

    var iconServerId: number | undefined = id ? formData.icon : undefined;
    try {
      // upload the new icon, if it exists (situation managed by the UploadManager)
      const uploadResult = await uploadManager.uploadFiles();
      console.log(uploadResult);
      if (uploadResult instanceof Array) {
        iconServerId = uploadResult[0].serverId;
      }
    } catch (exception) {
      console.log(exception);
    }

    var body = new MyList(
      authManager.getUserID()!!,
      formData.name,
      formData.description ? formData.description : undefined,
      iconServerId,
    );

    // check if we have EDIT or CREATE
    if (id) {
      body.id = parseInt(id);
      apiClient
        ?.put({
          url: APIEndPoints.MY_LISTS.ONE(parseInt(id)),
          body: body,
        })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "List updated successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.MY_LISTS,
              id: parseInt(id),
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    } else {
      apiClient
        ?.post<MyList>({ url: APIEndPoints.MY_LISTS.NEW, body: body })
        .then((response) => {
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "List created successfully",
          });
          navigate(
            SETTINGS.get_path({
              section: AppSections.MY_LISTS,
              id: response.id,
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    }
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.MY_LISTS.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "List  deleted successfully",
        });
        navigate(SETTINGS.get_path({ section: AppSections.MY_LISTS }));
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete this list. Try again later!",
          });
        }
      });
  };

  const formInputChange = (inputID: string, newValue: string) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy[inputID] = newValue;
    setFormData(formDataCopy as MyList);
  };

  const formIconChange = (
    id: string,
    value: string | undefined,
    files: FileList | null,
  ) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy["icon_path"] = value;
    formDataCopy["icon"] = -1;
    uploadManager.replaceFiles(id, UploadFileType.ICON, files);
    setFormData(formDataCopy as MyList);
  };

  const onFormCancel = () => {
    navigate(SETTINGS.go_back({ section: AppSections.MY_LISTS }));
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      console.log(serverError);
      console.log(apiErrors);
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check and try again.",
      );
    } catch (exception) {
      console.log(exception);
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const pageTitle = id ? "Edit list" : "Create a new list";
  const confirmLabel = id ? "Update list" : "Create list";

  const deleteButton = id ? (
    <Button
      label={"Delete list"}
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message: "Are you sure you want to delete this list ?",
          cancelLabel: "Cancel",
          confirmLabel: "Confirm",
          onConfirm: handleDelete,
        });
      }}
    />
  ) : null;

  if (loading) {
    return <Loading />;
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      headButton={deleteButton}
      hint="my lists"
    >
      <FormInputImage
        label="Picture"
        id="icon"
        placeholderImage=""
        value={ImgUtil.display_icon(formData.icon_path)}
        required={false}
        onChange={formIconChange}
      />

      <FormInputText
        label="List name"
        id="name"
        value={formData.name}
        required={true}
        placeholder="Schönbrunn Palace"
        onChange={formInputChange}
        error={formErrors.name}
      />

      <FormTextArea
        label="Description"
        id="description"
        value={formData.description}
        required={false}
        onChange={formInputChange}
        error={formErrors.description}
      />

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>{errorMessage}</div>
        </div>
      )}

      {/* License Agreement group */}
      <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
        <label className="text-sm">{EXPLANATION_HEADER}</label>

        <label className="flex items-center gap-2 text-sm mt-1">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <span>
            I agree with the{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alertsManager.showDialogWindow({
                  type: DialogWindowType.LICENSE,
                  message: "license",
                });
              }}
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Terms and Conditions
            </a>
          </span>
        </label>
      </div>

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />
        <Button
          label={confirmLabel}
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          disabled={!agreeTerms}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

function validateListForm(
  formData: MyList,
  groupingLabeL: string,
): IMyListsFormErrors {
  let errors: IMyListsFormErrors = {
    name: "",
    description: "",
  };

  errors.name =
    formData.name.length < 4
      ? groupingLabeL + " name should be at least 4 characters long"
      : "";
  /*errors.description =
    formData.description.length < 10
      ? groupingLabeL + " description should be at least 10 characters long"
      : "";*/

  return errors;
}

function isFormInvalid(errors: IMyListsFormErrors): boolean {
  return errors.name !== "" || errors.description !== "";
}

export default MyListAddEdit;
